import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import keyBy from "lodash/keyBy";

import db from "./fire/db";

Vue.use(Vuex);

const vuexLocal = new VuexPersistence({
  strictMode: process.env.NODE_ENV !== "production",
  storage: window.localStorage
});

function testCasesForSuit(state: any, suitId: any) {
  return state.testCases.filter(
    (testCase: any) => testCase.attrs.testSuitId == suitId
  );
}

export default new Vuex.Store<any>({
  strict: process.env.NODE_ENV !== "production",
  plugins: [vuexLocal.plugin],
  state: {
    user: null,
    loading: false,
    currentProject: null,
    projects: [],
    suitFolders: [],
    testSuits: [],
    currentSuit: null,
    testCases: [],
    executions: [],
    snackMessage: ""
  },
  mutations: {
    RESTORE_MUTATION: vuexLocal.RESTORE_MUTATION,
    loaded(state) {
      state.loading = false;
    },
    loading(state) {
      state.loading = true;
    },
    resetState(state) {
      state.user = null;
      state.currentProject = null;
      state.projects = [];
      state.testCases = [];
      state.executions = [];
    },
    setUser(state, user) {
      state.user = user;
    },
    addProject(state, project) {
      state.projects.push(project);
    },
    setProjects(state, projects) {
      state.projects = projects;
      if (state.currentProject == null && projects.length > 0) {
        state.currentProject = projects[0];
      }
    },
    deleteProject(state, id) {
      state.projects = state.projects.filter(
        (project: any) => project.id !== id
      );
    },
    setCurrentProject(state, project) {
      state.currentProject = project;
    },
    addTestSuit(state, suit) {
      state.testSuits.push(suit);
    },
    setTestSuits(state, testSuits) {
      state.testSuits = testSuits;
    },
    setCurrentSuit(state, suit) {
      state.currentSuit = suit;
    },
    deleteTestSuit(state, id) {
      state.testSuits = state.testSuits.filter(
        (testSuit: any) => testSuit.id !== id
      );
    },
    updateTestSuit(state, testSuit) {
      let suit: any = state.testSuits.find(
        (suit: any) => suit.id == testSuit.id
      );
      if (suit) {
        suit.name = testSuit.name;
      }
    },
    addTestCase(state, testCase) {
      state.testCases.push(testCase);
    },
    setTestCases(state, testCases) {
      state.testCases = testCases;
    },
    deleteTestCase(state, id) {
      state.testCases = state.testCases.filter(
        (testCase: any) => testCase.id !== id
      );
    },
    updateTestCase(state, { id, attrs }) {
      let testCase: any = state.testCases.find(
        (testCase: any) => testCase.id == id
      );
      if (testCase) {
        testCase.attrs = cloneDeep(attrs);
      }
    },
    setExecutions(state, executions) {
      state.executions = executions;
    },
    addExecution(state, execution) {
      state.executions.push(execution);
    },
    updateExecution(state, execution) {
      const index = state.executions.findIndex(
        (e: any) => e.id == execution.id
      );
      state.executions.splice(index, 1);
    },
    setSnack(state, message) {
      state.snackMessage = message;
    }
  },
  actions: {
    setUser({ commit, dispatch }, authUser) {
      const user = {
        name: authUser.displayName,
        uid: authUser.uid
      };
      commit("setUser", user);
      db.ensureUser(user);
      commit("loading");
      dispatch("loadTestCases");
    },
    unsetUser({ commit }) {
      commit("resetState");
    },
    createProject({ commit }, attrs) {
      db.projects()
        .create(cloneDeep(attrs))
        .then((project: any) => commit("addProject", project));
    },
    deleteProject({ state, commit }, id) {
      if (state.currentProject.id == id) {
        commit("setSnack", "Cannot remove current project.");
        return;
      }
      db.projects().delete(id);
      commit("deleteProject", id);
      commit("setSnack", "Project successfully deleted.");
    },
    deleteTestCase({ commit }, id) {
      db.testCases().delete(id);
      commit("deleteTestCase", id);
      commit("setSnack", "Test case successfully deleted.");
    },
    loadTestCases({ state, commit }) {
      if (state.loading) {
        Promise.all([
          db
            .testSuits()
            .all()
            .then((testSuits: any[]) =>
              commit("setTestSuits", [
                { name: "Default", id: "default" },
                ...testSuits
              ])
            ),
          db
            .testCases()
            .all()
            .then((testCases: any[]) => commit("setTestCases", testCases)),
          db
            .executions()
            .all()
            .then((executions: any[]) => commit("setExecutions", executions)),
          db
            .projects()
            .all()
            .then((projects: any[]) => commit("setProjects", projects))
        ]).then(() => commit("loaded"));
      }
    },
    createTestSuit({ commit }, name) {
      return db
        .testSuits()
        .create({ name: name })
        .then(function(testSuit) {
          commit("addTestSuit", testSuit);
          commit("setSnack", "Test suit successfully created.");
        });
    },
    updateTestSuit({ commit }, suit: any) {
      return db
        .testSuits()
        .update(suit.id, suit)
        .then(testSuit => {
          commit("updateTestSuit", testSuit);
          commit("setSnack", "Test suit successfully updated.");
        });
    },
    duplicateTestSuit({ state, commit }, { suit, newName }) {
      console.log(arguments);
      commit("loading");
      db.testSuits()
        .create({ name: newName })
        .then(function(testSuit) {
          commit("addTestSuit", testSuit);
          state.testCases.forEach((testCase: any) => {
            if (testCase.attrs.testSuitId === suit.id) {
              db.testCases()
                .create({ ...testCase.attrs, testSuitId: testSuit.id })
                .then(function(testCase) {
                  commit("addTestCase", testCase);
                });
            }
          });
          commit("loaded");
          commit("setSnack", "Test suit successfully duplicated.");
        });
    },
    deleteTestSuit({ state, commit }, suit: any) {
      if (testCasesForSuit(state, suit.id).length > 0) {
        commit("setSnack", "Cannot delete non-empty test suit.");
        return;
      }

      db.testSuits().delete(suit.id);
      commit("deleteTestSuit", suit.id);
      commit("setSnack", "Successfully deleted test suit.");
    },
    createTestCase({ commit }, attrs) {
      return db
        .testCases()
        .create(attrs)
        .then(function(testCase) {
          commit("addTestCase", testCase);
          commit("setSnack", "Test case successfully created.");
        });
    },
    updateTestCase({ commit }, { testCase, attrs }) {
      return db
        .testCases()
        .update(testCase.id, attrs)
        .then(function(testCase) {
          commit("updateTestCase", testCase);
          commit("setSnack", "Test case successfully updated.");
        });
    },
    createExecution({ commit }, testCases) {
      let attrs = {
        testCases: testCases.map((testCase: any) => ({
          id: testCase.id,
          ...cloneDeep(testCase.attrs)
        }))
      };
      db.executions()
        .create(attrs)
        .then(execution => commit("addExecution", execution));
    },
    updateExecution: debounce(function({ commit }, { id, ...attrs }) {
      db.executions()
        .update(id, cloneDeep(attrs))
        .then(execution => commit("updateExecution", execution));
    }, 300)
  },
  getters: {
    testSuiteTree(state) {
      // const folders = state.suitFolders.sort((folder) => folder.path)
      // const foldersByPath = keyBy(folders, 'path')
      // let tree = [];
      // folders.forEach((folder) => {
      //
      // });
      return state.testSuits;
    },
    testCasesForCurrentSuit(state: any) {
      if (state.currentSuit) {
        return testCasesForSuit(state, state.currentSuit);
      } else {
        return [];
      }
    }
  }
});
